import BigNumber from 'bignumber.js';
import { cloneDeep, find, get, includes, intersection, property, set } from 'lodash';
import { select, take } from 'redux-saga/effects';

import { ASSET_TYPE, FIELD_PATHS, FIELD_VALUES, LIABILITY_TYPE, REDUX_STORE } from '@funnels/constants';
import {
  Asset,
  Company,
  CustomerData,
  Expenses,
  IDlessAsset,
  IDlessLiability,
  Income,
  Liability,
  Owner,
  ReduxStoreData,
  ServerAssetFetch,
  ServerEmploymentFetch,
  ServerLiabilityCreate,
  ServerLiabilityFetch,
  StoreApplicant,
  StoreApplication,
  StoreEmployment,
} from '@funnels/types';
import { apiUtil } from '@funnels/utils';

export function* waitForAuth() {
  if (!apiUtil.getToken()) {
    yield take(REDUX_STORE.AUTHENTICATION.ACTION_TYPES.AUTH_SUCCESS);
  }
}

interface ResourceIds {
  applications?: string[];
  applicants?: string[];
  assets?: string[];
  companies?: string[];
  employments?: string[];
  incomes?: string[];
  liabilities?: string[];
  expenses?: string[];
}
type ActionHandler = (action: any, counter: number, resourceIds: ResourceIds) => number;
function defaultActionHandler(action: any, counter: number, resourceIds: ResourceIds) {
  const {
    applications = [],
    applicants = [],
    assets = [],
    companies = [],
    liabilities = [],
    incomes = [],
    employments = [],
    expenses = [],
  } = resourceIds;

  const isApplication =
    action.type === REDUX_STORE.APPLICATIONS.ACTION_TYPES.CREATE_APPLICATION_SUCCESS &&
    applications.length > 0 &&
    includes(applications, action.clientId);
  const isApplicant =
    action.type === REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_SUCCESS &&
    applicants.length > 0 &&
    includes(applicants, action.clientId);
  const isAsset =
    action.type === REDUX_STORE.ASSETS.ACTION_TYPES.CREATE_ASSET_SUCCESS &&
    assets.length > 0 &&
    includes(assets, action.clientId);
  const isLiability =
    action.type === REDUX_STORE.LIABILITIES.ACTION_TYPES.CREATE_LIABILITY_SUCCESS &&
    liabilities.length > 0 &&
    includes(liabilities, action.clientId);
  const isIncome =
    action.type === REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_SUCCESS &&
    incomes.length > 0 &&
    incomes.includes(action.clientId);
  const isEmployments =
    (action.type === REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_V2_EMPLOYMENTS_SUCCESS ||
      action.type === REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_SUCCESS) &&
    employments.length > 0;
  const isExpenses =
    action.type === REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_SUCCESS &&
    expenses.length > 0 &&
    expenses.includes(action.clientId);
  const isCompanies =
    action.type === REDUX_STORE.COMPANIES.ACTION_TYPES.CREATE_COMPANY_SUCCESS &&
    companies.length > 0 &&
    companies.includes(action.clientId);
  const isIncomeAbort = action.type === REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_ABORT;
  const isEmploymentAbort = action.type === REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_ABORT;

  if (
    isApplication ||
    isApplicant ||
    isAsset ||
    isLiability ||
    isIncome ||
    isEmployments ||
    isExpenses ||
    isCompanies ||
    isIncomeAbort ||
    isEmploymentAbort
  ) {
    return counter - 1;
  }

  return counter;
}
/**
 * NOTE: make sure you use clientId
 * @param resourceIds arrays of ids you would like to wait for
 * @param actionHandler a function to handle taken action, return a number of resources to load after handle action
 */
export function* waitForResourcesToBeCreated(
  resourceIds: ResourceIds,
  actionHandler: ActionHandler = defaultActionHandler
): any {
  const { applications, applicants, liabilities, assets, incomes, employments, expenses, companies } = resourceIds;
  const events = [];
  let resourcesToLoad = 0;

  if (applications?.length) {
    resourcesToLoad += applications.length;
    events.push(REDUX_STORE.APPLICATIONS.ACTION_TYPES.CREATE_APPLICATION_SUCCESS);
  }
  if (applicants?.length) {
    resourcesToLoad += applicants.length;
    events.push(REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_SUCCESS);
  }
  if (liabilities?.length) {
    resourcesToLoad += liabilities.length;
    events.push(REDUX_STORE.LIABILITIES.ACTION_TYPES.CREATE_LIABILITY_SUCCESS);
  }
  if (assets?.length) {
    resourcesToLoad += assets.length;
    events.push(REDUX_STORE.ASSETS.ACTION_TYPES.CREATE_ASSET_SUCCESS);
  }
  if (incomes?.length) {
    resourcesToLoad += incomes.length;
    events.push(REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_SUCCESS);
  }
  if (expenses?.length) {
    resourcesToLoad += expenses.length;
    events.push(REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_SUCCESS);
  }
  if (companies?.length) {
    resourcesToLoad += companies.length;
    events.push(REDUX_STORE.COMPANIES.ACTION_TYPES.CREATE_COMPANY_SUCCESS);
    // take abort when company create fail
    events.push(REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_ABORT);
    events.push(REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_ABORT);
  }

  const loadedResources = yield select((state: ReduxStoreData) => {
    let loadedResources = 0;
    const getClientId = property('clientId');
    const getApplicantId = property('applicantId');

    if (applications?.length) {
      loadedResources += intersection(
        applications,
        get(state, 'applications.data', [])
          .filter((o: StoreApplication) => o.id)
          .map(getClientId)
      ).length;
    }

    if (applicants?.length) {
      loadedResources += intersection(
        applicants,
        get(state, 'applicants.data', [])
          .filter((o: StoreApplicant) => o.id)
          .map(getClientId)
      ).length;
    }

    if (liabilities?.length) {
      loadedResources += intersection(
        liabilities,
        get(state, 'liabilities.data', [])
          .filter((o: Liability) => o.id)
          .map(getClientId)
      ).length;
    }

    if (assets?.length) {
      loadedResources += intersection(
        assets,
        get(state, 'assets.data', [])
          .filter((o: Asset) => o.id)
          .map(getClientId)
      ).length;
    }

    if (incomes?.length) {
      loadedResources += intersection(
        incomes,
        get(state, 'incomes.data', [])
          .filter((i: Income) => i.id)
          .map(getClientId)
      ).length;
    }

    if (employments?.length) {
      const storeEmployments = get(state, 'employments.data', []);

      // we needs to know how many action we send base on applicant
      const applicantIdsToLoad = storeEmployments.map((employment: StoreEmployment) =>
        get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0))
      );
      resourcesToLoad += intersection(
        applicantIdsToLoad,
        storeEmployments.filter((e: StoreEmployment) => !e.id).map(getApplicantId)
      ).length;
      events.push(REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_SUCCESS);
      events.push(REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_V2_EMPLOYMENTS_SUCCESS);
    }

    if (expenses?.length) {
      loadedResources += intersection(
        expenses,
        get(state, 'expenses.data', [])
          .filter((e: Expenses) => e.id)
          .map(getClientId)
      ).length;
    }

    if (companies?.length) {
      loadedResources += intersection(
        companies,
        get(state, 'companies.data', [])
          .filter((e: Company) => e.id)
          .map(getClientId)
      ).length;
    }
    return loadedResources;
  });

  resourcesToLoad -= loadedResources;

  while (resourcesToLoad > 0) {
    const action = yield take(events);
    resourcesToLoad = actionHandler(action, resourcesToLoad, resourceIds);
  }
}

type ResourceList =
  | 'applications'
  | 'applicants'
  | 'assets'
  | 'liabilities'
  | 'employments'
  | 'incomes'
  | 'expenses'
  | 'companies'
  | 'relatedParties';
type ResourcesList = ResourceList[];

export function* waitForResourcesToBeFree(list: ResourcesList) {
  const events = [];
  let busyResources = 0;

  function* checkResources(): any {
    const storeState = yield select((state: ReduxStoreData) => state);
    busyResources = 0;

    if (list.includes('applications') && storeState.applications.loading > 0) {
      busyResources++;
    }

    if (list.includes('applicants') && storeState.applicants.loading > 0) {
      busyResources++;
    }

    if (list.includes('assets') && storeState.assets.loading > 0) {
      busyResources++;
    }

    if (list.includes('liabilities') && storeState.liabilities.loading > 0) {
      busyResources++;
    }

    if (list.includes('employments') && storeState.employments.loading > 0) {
      busyResources++;
    }

    if (list.includes('incomes') && storeState.incomes.loading > 0) {
      busyResources++;
    }

    if (list.includes('expenses') && storeState.expenses.loading > 0) {
      busyResources++;
    }

    if (list.includes('companies') && storeState.companies.loading > 0) {
      busyResources++;
    }

    if (list.includes('relatedParties') && storeState.relatedParties.loading > 0) {
      busyResources++;
    }
  }

  if (list.includes('applications')) {
    events.push(
      REDUX_STORE.APPLICATIONS.ACTION_TYPES.CREATE_APPLICATION_FAIL,
      REDUX_STORE.APPLICATIONS.ACTION_TYPES.CREATE_APPLICATION_SUCCESS,
      REDUX_STORE.APPLICATIONS.ACTION_TYPES.UPDATE_APPLICATION_FAIL,
      REDUX_STORE.APPLICATIONS.ACTION_TYPES.UPDATE_APPLICATION_SUCCESS
    );
  }

  if (list.includes('applicants')) {
    events.push(
      REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_FAIL,
      REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_SUCCESS,
      REDUX_STORE.APPLICANTS.ACTION_TYPES.UPDATE_APPLICANT_FAIL,
      REDUX_STORE.APPLICANTS.ACTION_TYPES.UPDATE_APPLICANT_SUCCESS,
      REDUX_STORE.APPLICANTS.ACTION_TYPES.DELETE_APPLICANT_FAIL,
      REDUX_STORE.APPLICANTS.ACTION_TYPES.DELETE_APPLICANT_SUCCESS,
      REDUX_STORE.APPLICANTS.ACTION_TYPES.FETCH_APPLICANTS_SUCCESS,
      REDUX_STORE.APPLICANTS.ACTION_TYPES.FETCH_APPLICANTS_FAIL
    );
  }

  if (list.includes('assets')) {
    events.push(
      REDUX_STORE.ASSETS.ACTION_TYPES.CREATE_ASSET_FAIL,
      REDUX_STORE.ASSETS.ACTION_TYPES.CREATE_ASSET_SUCCESS,
      REDUX_STORE.ASSETS.ACTION_TYPES.UPDATE_ASSET_FAIL,
      REDUX_STORE.ASSETS.ACTION_TYPES.UPDATE_ASSET_SUCCESS,
      REDUX_STORE.ASSETS.ACTION_TYPES.DELETE_ASSET_FAIL,
      REDUX_STORE.ASSETS.ACTION_TYPES.DELETE_ASSET_SUCCESS
    );
  }

  if (list.includes('liabilities')) {
    events.push(
      REDUX_STORE.LIABILITIES.ACTION_TYPES.CREATE_LIABILITY_FAIL,
      REDUX_STORE.LIABILITIES.ACTION_TYPES.CREATE_LIABILITY_SUCCESS,
      REDUX_STORE.LIABILITIES.ACTION_TYPES.UPDATE_LIABILITY_FAIL,
      REDUX_STORE.LIABILITIES.ACTION_TYPES.UPDATE_LIABILITY_SUCCESS,
      REDUX_STORE.LIABILITIES.ACTION_TYPES.DELETE_LIABILITY_FAIL,
      REDUX_STORE.LIABILITIES.ACTION_TYPES.DELETE_LIABILITY_SUCCESS
    );
  }

  if (list.includes('employments')) {
    events.push(
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_FAIL,
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_SUCCESS,
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_V2_EMPLOYMENTS_FAIL,
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_V2_EMPLOYMENTS_SUCCESS,
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPSERT_V2_EMPLOYMENTS_FAIL,
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPSERT_V2_EMPLOYMENTS_SUCCESS,
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.DELETE_EMPLOYMENTS_FAIL,
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.DELETE_EMPLOYMENTS_SUCCESS
    );
  }

  if (list.includes('incomes')) {
    events.push(
      REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_FAIL,
      REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_SUCCESS,
      REDUX_STORE.INCOMES.ACTION_TYPES.UPDATE_INCOME_FAIL,
      REDUX_STORE.INCOMES.ACTION_TYPES.UPDATE_INCOME_SUCCESS,
      REDUX_STORE.INCOMES.ACTION_TYPES.DELETE_INCOME_FAIL,
      REDUX_STORE.INCOMES.ACTION_TYPES.DELETE_INCOME_SUCCESS
    );
  }

  if (list.includes('expenses')) {
    events.push(
      REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_FAIL,
      REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_SUCCESS,
      REDUX_STORE.EXPENSES.ACTION_TYPES.UPDATE_EXPENSES_FAIL,
      REDUX_STORE.EXPENSES.ACTION_TYPES.UPDATE_EXPENSES_SUCCESS,
      REDUX_STORE.EXPENSES.ACTION_TYPES.DELETE_EXPENSES_FAIL,
      REDUX_STORE.EXPENSES.ACTION_TYPES.DELETE_EXPENSES_SUCCESS
    );
  }

  if (list.includes('companies')) {
    events.push(
      REDUX_STORE.COMPANIES.ACTION_TYPES.CREATE_COMPANY_FAIL,
      REDUX_STORE.COMPANIES.ACTION_TYPES.CREATE_COMPANY_SUCCESS,
      REDUX_STORE.COMPANIES.ACTION_TYPES.UPDATE_COMPANY_FAIL,
      REDUX_STORE.COMPANIES.ACTION_TYPES.UPDATE_COMPANY_SUCCESS,
      REDUX_STORE.COMPANIES.ACTION_TYPES.DELETE_COMPANY_FAIL,
      REDUX_STORE.COMPANIES.ACTION_TYPES.DELETE_COMPANY_SUCCESS
    );
  }

  if (list.includes('relatedParties')) {
    events.push(
      REDUX_STORE.RELATED_PARTY.ACTION_TYPES.CREATE_RELATED_PARTY_FAIL,
      REDUX_STORE.RELATED_PARTY.ACTION_TYPES.CREATE_RELATED_PARTY_SUCCESS,
      REDUX_STORE.RELATED_PARTY.ACTION_TYPES.UPDATE_RELATED_PARTY_FAIL,
      REDUX_STORE.RELATED_PARTY.ACTION_TYPES.UPDATE_RELATED_PARTY_SUCCESS,
      REDUX_STORE.RELATED_PARTY.ACTION_TYPES.DELETE_RELATED_PARTY_FAIL,
      REDUX_STORE.RELATED_PARTY.ACTION_TYPES.DELETE_RELATED_PARTY_SUCCESS
    );
  }

  yield checkResources();

  while (busyResources > 0) {
    yield take(events);
    yield checkResources();
  }
}

export function* getApplicationId(clientId: string, id?: string): any {
  if (!id) {
    const reduxStoreApplication: StoreApplication[] = yield select((state: ReduxStoreData) => state.applications.data);
    const application = find(reduxStoreApplication, { clientId });
    if (application && application.id) {
      return application.id;
    }
  }
  while (!id) {
    const action = yield take([
      REDUX_STORE.APPLICATIONS.ACTION_TYPES.CREATE_APPLICATION_FAIL,
      REDUX_STORE.APPLICATIONS.ACTION_TYPES.CREATE_APPLICATION_SUCCESS,
    ]);

    if (action.clientId === clientId) {
      // Create failed, return undefined
      if (action.type === REDUX_STORE.APPLICATIONS.ACTION_TYPES.CREATE_APPLICATION_FAIL) {
        return;
      }
      id = action.id;
    }
  }

  return id;
}

export function* getApplicantId(clientId: string, id?: string): any {
  if (!id) {
    const reduxStoreApplicants: StoreApplicant[] = yield select((state: ReduxStoreData) => state.applicants.data);
    const applicant = find(reduxStoreApplicants, { clientId });
    if (applicant && applicant.id) {
      return applicant.id;
    }
  }
  while (!id) {
    const action = yield take([
      REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_FAIL,
      REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_SUCCESS,
    ]);
    if (action.clientId === clientId) {
      // Create failed, return undefined
      if (action.type === REDUX_STORE.APPLICANTS.ACTION_TYPES.CREATE_APPLICANT_FAIL) {
        return;
      }
      id = action.id;
    }
  }

  return id;
}

export function* getAssetId(clientId: string, id?: string): any {
  if (!id) {
    const reduxStoreAssets: Asset[] = yield select((state: ReduxStoreData) => state.assets.data);
    const asset = find(reduxStoreAssets, { clientId });
    if (asset && asset.id) {
      return asset.id;
    }
  }
  while (!id) {
    const action = yield take([
      REDUX_STORE.ASSETS.ACTION_TYPES.CREATE_ASSET_FAIL,
      REDUX_STORE.ASSETS.ACTION_TYPES.CREATE_ASSET_SUCCESS,
    ]);
    if (action.clientId === clientId) {
      // Create failed, return undefined
      if (action.type === REDUX_STORE.ASSETS.ACTION_TYPES.CREATE_ASSET_FAIL) {
        return;
      }
      id = action.id;
    }
  }
  return id;
}

export function* getLiabilityId(clientId: string, id?: string): any {
  if (!id) {
    const reduxStoreLiabilities: Liability[] = yield select((state: ReduxStoreData) => state.liabilities.data);
    const liability = find(reduxStoreLiabilities, { clientId });
    if (liability && liability.id) {
      return liability.id;
    }
  }
  while (!id) {
    const action = yield take([
      REDUX_STORE.LIABILITIES.ACTION_TYPES.CREATE_LIABILITY_FAIL,
      REDUX_STORE.LIABILITIES.ACTION_TYPES.CREATE_LIABILITY_SUCCESS,
    ]);

    if (action.clientId === clientId) {
      // Create failed, return undefined
      if (action.type === REDUX_STORE.LIABILITIES.ACTION_TYPES.CREATE_LIABILITY_FAIL) {
        return;
      }
      id = action.id;
    }
  }

  return id;
}

export function* getExpensesApplicantId(clientId: string, applicantId?: string): any {
  if (!applicantId) {
    const reduxStoreExpenses: Expenses[] = yield select((state: ReduxStoreData) => state.expenses.data);
    const expenses = find(reduxStoreExpenses, { clientId });
    if (expenses && expenses.applicantId) {
      return expenses.applicantId;
    }
  }
  while (!applicantId) {
    const action = yield take([
      REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_FAIL,
      REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_SUCCESS,
    ]);
    if (action.clientId === clientId) {
      // Create failed, return undefined
      if (action.type === REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_FAIL) {
        return;
      }
      applicantId = action.applicantId;
    }
  }
  return applicantId;
}

export function* getIncomeId(clientId: string, id?: string): any {
  if (!id) {
    const reduxStoreIncomes: Income[] = yield select((state: ReduxStoreData) => state.incomes.data);
    const income = find(reduxStoreIncomes, { clientId });
    if (income && income.id) {
      return income.id;
    }
  }
  while (!id) {
    const action = yield take([
      REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_FAIL,
      REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_SUCCESS,
    ]);
    if (action.clientId === clientId) {
      // Create failed, return undefined
      if (action.type === REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME_FAIL) {
        return;
      }
      id = action.id;
    }
  }
  return id;
}

export function* getExpensesId(clientId: string, id?: string): any {
  if (!id) {
    const reduxStoreExpenses: Expenses[] = yield select(({ expenses: { data } }: ReduxStoreData) => data);
    const expenses = reduxStoreExpenses.find(
      ({ clientId: expensesClientId }: Expenses) => expensesClientId === clientId
    );

    if (expenses && expenses.id) {
      return expenses.id;
    }
  }
  while (!id) {
    const action = yield take([
      REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_FAIL,
      REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_SUCCESS,
    ]);
    if (action.clientId === clientId) {
      // Create failed, return undefined
      if (action.type === REDUX_STORE.EXPENSES.ACTION_TYPES.CREATE_EXPENSES_FAIL) {
        return;
      }
      id = action.id;
    }
  }
  return id;
}

export function* getEmploymentId(clientId: string, id?: string): any {
  if (!id) {
    const reduxStoreEmployments: StoreEmployment[] = yield select((state: ReduxStoreData) => state.employments.data);
    const employment = find(reduxStoreEmployments, { clientId });

    if (employment && employment.id) {
      return employment.id;
    }
  }

  while (!id) {
    const action = yield take([
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_FAIL,
      REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_SUCCESS,
    ]);
    const match = find(action.employments, { clientId });

    if (match) {
      // Create failed, return undefined
      if (action.type === REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_EMPLOYMENTS_FAIL) {
        return;
      }

      id = match.id;
    }
  }

  return id;
}

export function sortByCreatedDateAsc(o1: any, o2: any) {
  const date1 = new Date(o1.createdAt);
  const date2 = new Date(o2.createdAt);
  return date1.getTime() - date2.getTime();
}

export function getEmploymentSortedByType(employments: ServerEmploymentFetch[]) {
  const primaryEmployments = employments
    .filter(
      (employment: ServerEmploymentFetch) =>
        get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_TYPE(0)) === FIELD_VALUES.PRIMARY
    )
    .sort(sortByCreatedDateAsc);
  const secondaryEmployments = employments
    .filter(
      (employment: ServerEmploymentFetch) =>
        get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_TYPE(0)) === FIELD_VALUES.SECONDARY
    )
    .sort(sortByCreatedDateAsc);
  const previousEmployments = employments
    .filter(
      (employment: ServerEmploymentFetch) =>
        ![FIELD_VALUES.PRIMARY, FIELD_VALUES.SECONDARY].includes(
          get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_TYPE(0))
        )
    )
    .sort(
      (
        { startDate: aStartDate = '', endDate: aEndDate = '' },
        { startDate: bStartDate = '', endDate: bEndDate = '' }
      ) => bEndDate.localeCompare(aEndDate) || bStartDate.localeCompare(aStartDate)
    );

  return [...primaryEmployments, ...secondaryEmployments, ...previousEmployments];
}

const TYPES_TO_SKIP_FIX = [ASSET_TYPE.PROPERTY, LIABILITY_TYPE.HOME_LOAN];
export function fixOwnershipForAPI(item: IDlessAsset | ServerAssetFetch | ServerLiabilityCreate) {
  const owners: Owner[] = get(item, 'percentOwned.owners', []);
  const shouldFixOwners = !TYPES_TO_SKIP_FIX.includes(item.type) && owners.length;
  if (shouldFixOwners) {
    const newItem = cloneDeep(item);
    const newOwners = owners.filter((o) => o.applicantId);
    set(newItem, 'percentOwned.owners', newOwners);
    return newItem;
  }
  return item;
}

export function fixOwnershipFromAPI(item: IDlessAsset | ServerAssetFetch | IDlessLiability | ServerLiabilityFetch) {
  const newOwners: Owner[] = get(item, 'percentOwned.owners', []);
  const shouldFixOwners = !TYPES_TO_SKIP_FIX.includes(item.type) && newOwners.length;
  if (shouldFixOwners) {
    let totalOwnershipPercent = 0;
    newOwners.forEach((o) => {
      totalOwnershipPercent += o.percent;
    });
    if (totalOwnershipPercent > 0 && totalOwnershipPercent < 1) {
      newOwners.push({
        percent: 1 - totalOwnershipPercent,
        applicantId: '',
        customerId: '',
      });
      set(item, 'percentOwned.owners', newOwners);
      return item;
    }
  }
  return item;
}

export function removeClientSideOwnershipReference(
  item: Liability | Asset | Income,
  applicants: StoreApplicant[],
  hasCustomerId: boolean = true
) {
  if (!item.percentOwned?.owners?.length) {
    return item;
  }

  return {
    ...item,
    percentOwned: {
      ...item.percentOwned,
      owners: item.percentOwned.owners.map((o) => {
        if (!o.applicantClientId) {
          return o;
        }
        const ownertShip = {
          percent: o.percent || 0,
          applicantId: (applicants.find((a) => a.clientId === o.applicantClientId) || {}).id || '',
        };

        if (hasCustomerId) {
          const ownerCustomerId = (applicants.find((a) => a.clientId === o.applicantClientId) || {}).customerId;
          if (!ownerCustomerId) {
            return ownertShip;
          }

          return {
            ...ownertShip,
            customerId: ownerCustomerId,
          };
        }

        return ownertShip;
      }),
    },
  };
}

export function addDefaultOwners(
  item: Liability | Asset,
  reduxStoreApplicants: StoreApplicant[],
  reduxStoreCustomer?: CustomerData,
  hasCustomerId: boolean = true
) {
  if (!!reduxStoreCustomer && !item.percentOwned?.owners?.length) {
    const { id: customerId } = reduxStoreCustomer;
    const customerApplicantType = reduxStoreApplicants.find(
      (applicant) => applicant.customerId === customerId
    )?.applicantType;

    const filteredApplicants = reduxStoreApplicants.filter(
      (applicant) => applicant.applicantType === customerApplicantType
    );

    const { owners, totalPercent } = filteredApplicants.reduce(
      (result, applicant) => {
        const { owners, totalPercent } = result;
        const percent = new BigNumber(1)
          .dividedBy(filteredApplicants.length)
          .precision(4, BigNumber.ROUND_FLOOR)
          .toNumber();
        owners.push({
          applicantId: applicant.id!,
          percent,
          ...(hasCustomerId && { customerId: applicant.customerId }),
        });
        return {
          owners,
          totalPercent: new BigNumber(percent).plus(totalPercent).precision(4, BigNumber.ROUND_FLOOR).toNumber(),
        };
      },
      { owners: [] as Owner[], totalPercent: 0 }
    );

    if (owners.length > 0 && totalPercent !== 1) {
      const remainder = new BigNumber(1).minus(totalPercent).precision(4, BigNumber.ROUND_FLOOR).toNumber();
      owners[0].percent = new BigNumber(owners[0].percent)
        .plus(remainder)
        .precision(4, BigNumber.ROUND_FLOOR)
        .toNumber();
    }

    const percentOwned = {
      proportions: 'NOT_SPECIFIED',
      owners,
    };
    return {
      ...item,
      percentOwned,
    };
  }

  return item;
}
